import { differenceInCalendarDays, isSameYear } from 'date-fns';
import type { TFunction } from 'i18next';

import type { Formatters } from '@/shared/i18n/format';

import type { Message, MessagePreview } from '../types';

/**
 * The compact time of a chat in lists (like messengers do): "14:05" today, "Вчера", the weekday within a week ("пн"),
 * "6 окт." this year, else the short date.
 */
export function listTime(
  value: string,
  fmt: Pick<Formatters, 'pattern' | 'language'>,
  labels: { yesterday: string },
  now: Date = new Date(),
): string {
  const date = new Date(value);
  const days = differenceInCalendarDays(now, date);
  if (days <= 0) return fmt.pattern(date, 'p');
  if (days === 1) return labels.yesterday;
  // "пн"/"вт" (ru, uk: the three-letter form is "втр"), "Mon"/"Tue" (en).
  if (days < 7) return fmt.pattern(date, fmt.language === 'en' ? 'EEE' : 'EEEEEE');
  return fmt.pattern(date, isSameYear(date, now) ? 'd MMM' : 'P');
}

/** "0:42", "23:06", "1:02:03". */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

interface SystemLike {
  systemType: string | null;
  systemParams: Record<string, string> | null;
  authorName: string | null;
}

/** The text of a system message in the reader's language (names come with the parameters). */
export function systemText(message: SystemLike, t: TFunction<'messenger'>): string {
  const p = message.systemParams ?? {};
  const actor = message.authorName ?? '';
  const video = p.video === 'true';
  switch (message.systemType) {
    case 'chat-created':
      return t('system.chatCreated', { actor, title: p.title ?? '' });
    case 'members-added':
      return t('system.membersAdded', { actor, names: p.userIdsNames ?? '' });
    case 'member-removed':
      return t('system.memberRemoved', { actor, name: p.userIdNames ?? '' });
    case 'member-left':
      return p.newOwnerIdNames
        ? t('system.memberLeftOwner', { actor, owner: p.newOwnerIdNames })
        : t('system.memberLeft', { actor });
    case 'title-changed':
      return t('system.titleChanged', { actor, title: p.title ?? '' });
    case 'avatar-changed':
      return t('system.avatarChanged', { actor });
    case 'group-chat-created':
      return t('system.groupChatCreated', { title: p.title ?? '' });
    case 'group-member-joined':
      return t('system.groupMemberJoined', { names: p.userIdsNames ?? '' });
    case 'group-member-left':
      return t('system.groupMemberLeft', { name: p.userIdNames ?? '' });
    case 'group-unlinked':
      return t('system.groupUnlinked');
    case 'role-changed':
      return t(p.role === 'admin' ? 'system.roleAdmin' : 'system.roleMember', {
        actor,
        name: p.userIdNames ?? '',
      });
    case 'settings-changed': {
      const policy = (value: string | undefined) =>
        value === 'admins' ? t('system.policyAdmins') : t('system.policyEveryone');
      return t('system.settingsChanged', { actor, addMembers: policy(p.addMembers), write: policy(p.write) });
    }
    case 'owner-changed':
      return t('system.ownerChanged', { name: p.userIdNames ?? '', owner: p.newOwnerIdNames ?? '' });
    case 'call-started':
      return t(video ? 'system.videoCallStarted' : 'system.callStarted', { actor });
    case 'call-ended': {
      const duration = Number(p.durationSeconds ?? '0');
      switch (p.outcome) {
        case 'completed':
          return t(video ? 'system.videoCallEnded' : 'system.callEnded', {
            duration: formatDuration(duration),
          });
        case 'declined':
          return t('system.callDeclined');
        case 'cancelled':
          return t('system.callCancelled', { actor });
        default:
          return t('system.callMissed', { actor });
      }
    }
    default:
      return t('system.unknown');
  }
}

export const systemTextOf = (message: Message, t: TFunction<'messenger'>) =>
  systemText({ ...message, authorName: message.author?.fullName ?? null }, t);

/** One line for the chat list: "Вы: текст", "Анна: 📎 Файл", system texts, deleted messages. */
export function previewText(
  preview: MessagePreview,
  options: { me: string; isGroup: boolean; isSaved?: boolean },
  t: TFunction<'messenger'>,
): string {
  if (preview.kind === 'system') return systemText(preview, t);
  if (preview.isDeleted) return t('message.deleted');
  const body = preview.text || (preview.hasAttachments ? `📎 ${t('message.attachment')}` : '');
  // Everything in the saved chat is the person's own: no "You:".
  if (options.isSaved) return body;
  if (preview.authorId === options.me) return t('list.you', { text: body });
  if (options.isGroup && preview.authorName) return `${firstName(preview.authorName)}: ${body}`;
  return body;
}

/** First word of a full name ("Анна Смирнова" → "Анна"). */
export const firstName = (fullName: string): string => fullName.trim().split(/\s+/u)[0] ?? fullName;
